"use client";

import React, { useEffect, useLayoutEffect, useRef } from "react";
import { motion, useAnimationControls, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

type State = "collapsed" | "expanded";

export const AnimatedAsset6 = ({
  className = "",
  language,
}: {
  className?: string;
  language: "en" | "de";
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = { ease: "circOut", duration: 0.75 };

  const shine = {
    collapsed: {
      y: 0,
      backgroundPosition: ["-60% 0%", "160% 0%"],
      transition: {
        y: ease,
        backgroundPosition: { ...ease, duration: 1.2 },
      },
    },
    expanded: {
      y: "-5.5rem",
      backgroundPosition: ["160% 0%", "-60% 0%"],
      transition: {
        y: ease,
        backgroundPosition: { ...ease, duration: 1.2 },
      },
    },
  };

  const bitunix = {
    collapsed: { opacity: 1, y: 0, transition: ease },
    expanded: { opacity: 1, y: "-5.5rem", transition: ease },
  };

  const pionex = {
    collapsed: { opacity: 1, transition: ease },
    expanded: { opacity: 1, transition: ease },
  };

  const phemex = {
    collapsed: { opacity: 1, y: 0, transition: ease },
    expanded: { opacity: 1, y: "5.5rem", transition: ease },
  };

  const bitget = {
    collapsed: { opacity: 1, y: 0, transition: ease },
    expanded: { opacity: 1, y: "11rem", transition: ease },
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[50rem] bg-[#0A1A1D] flex flex-col items-center justify-start px-[2rem] pt-[10rem]",
        "relative after:absolute after:h-[2px] after:w-full after:left-0 after:bottom-0 after:bg-[#D9F9B9]",
        className,
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.p
        className="w-[min(51rem,calc(100%-4rem)] relative mb-[2.5rem] text-[4.5rem] leading-[1.1em] font-medium text-center font-funnel text-white/40"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.4) 35%, #D9F9B9 45%, #FFFFFF 50%, #D9F9B9 55%, rgba(255,255,255,0.4) 65%, rgba(255,255,255,0.4) 100%)",
          backgroundSize: "300% 100%",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
          color: "transparent",
        }}
        variants={shine}
        animate={controls}
        initial="collapsed"
      >
        <span
          style={{
            WebkitTextFillColor: "rgba(255,255,255,0.4)",
            color: "rgba(255,255,255,0.4)",
          }}
        >
          {language === "en"
            ? "Your account is connected via"
            : "Dein Konto ist verbunden über"}
        </span>
      </motion.p>

      <div className="w-[31.875rem] relative flex flex-col-reverse gap-[.5rem]">
        <motion.div
          className="w-full flex items-start overflow-clip absolute left-0 top-0"
          variants={phemex}
          animate={controls}
          initial="collapsed"
        >
          <p className="-translate-y-px translate-x-px mb-0 h-[4.25rem] w-[2.75rem] bg-[#32CA0A] flex-shrink-0 [clip-path:polygon(0_0,100%_0,100%_100%,1.5rem_100%,0_calc(100%-1.5rem))] leading-none flex items-center justify-center font-geistmono font-medium text-black text-[2rem]">
            3
          </p>

          <div className="bg-[#112628] min-h-[5rem] flex items-center justify-center gap-[1rem] grow border-l-[2px] border-l-[#32CA0A]">
            <span className="w-[1.125rem] h-[1.125rem] rounded-full bg-[#32CA0A]" />
            <span className="text-white/80 font-[600] text-[2.5rem]">
              Exchange 3
            </span>
          </div>
        </motion.div>

        <motion.div
          className="w-full flex items-start overflow-clip absolute left-0 top-0"
          variants={pionex}
          animate={controls}
          initial="collapsed"
        >
          <p className="-translate-y-px translate-x-px mb-0 h-[4.25rem] w-[2.75rem] bg-[#FF8D28] flex-shrink-0 [clip-path:polygon(0_0,100%_0,100%_100%,1.5rem_100%,0_calc(100%-1.5rem))] leading-none flex items-center justify-center font-geistmono font-medium text-black text-[2rem]">
            2
          </p>

          <div className="bg-[#112628] min-h-[5rem] flex items-center justify-center gap-[1rem] grow border-l-[2px] border-l-[#FF8D28]">
            <span className="w-[1.125rem] h-[1.125rem] rounded-full bg-[#FF8D28]" />
            <span className="text-white/80 font-[600] text-[2.5rem]">
              Exchange 2
            </span>
          </div>
        </motion.div>

        <motion.div
          className="w-full flex items-start overflow-clip relative"
          variants={bitunix}
          animate={controls}
          initial="collapsed"
        >
          <p className="-translate-y-px translate-x-px mb-0 h-[4.25rem] w-[2.75rem] bg-[#D9F9B9] flex-shrink-0 [clip-path:polygon(0_0,100%_0,100%_100%,1.5rem_100%,0_calc(100%-1.5rem))] leading-none flex items-center justify-center font-geistmono font-medium text-black text-[2rem]">
            1
          </p>

          <div className="bg-[#112628] min-h-[5rem] flex items-center justify-center gap-[1rem] grow border-l-[2px] border-l-[#D9F9B9]">
            <span className="w-[1.125rem] h-[1.125rem] rounded-full bg-[#D9F9B9]" />
            <span className="text-white/80 font-[600] text-[2.5rem]">
              Exchange 1
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
